import React, { useState, useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import { filterData, formatValue } from '@ts-core/utils/dataTableUtils';

import TSTable from '@ts-core/components/Table/TSTable';
import { TSTableColumn } from '@ts-core/components/Table/TSTable/TSTable';

import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';

import InfoIcon from '@mui/icons-material/Info';

import { CourseSubjectWithStats } from '../../types/stats';

interface CourseStatsTableProps {
  subjects?: CourseSubjectWithStats[];
  loading: boolean;
  error?: string;

  xlsxFileName?: string;

  onSelectSubject?: (subject: CourseSubjectWithStats) => void;
  onRefetchStats?: () => void;
}

const CourseStatsTable = (props: CourseStatsTableProps) => {
  const intl = useIntl();

  const [query, setQuery] = useState('');
  const { xlsxFileName, subjects, error, loading, onRefetchStats, onSelectSubject } = props;

  const columns = useMemo((): TSTableColumn<CourseSubjectWithStats>[] => {
    const answerTypesColumns: TSTableColumn<CourseSubjectWithStats>[] = [
      {
        Header: intl.formatMessage({ id: 'positives' }),
        value: intl.formatMessage({ id: 'positives' }),
        accessor: 'stats.positiveRatio',
        valueTransformer: (value) => formatValue(value, 1, true, 1),
        sortable: true,
      },
      {
        Header: intl.formatMessage({ id: 'negatives' }),
        value: intl.formatMessage({ id: 'negatives' }),
        accessor: 'stats.negativeRatio',
        valueTransformer: (value) => formatValue(value, 1, true, 1),
        sortable: true,
      },
      {
        Header: intl.formatMessage({ id: 'neutrals' }),
        value: intl.formatMessage({ id: 'neutrals' }),
        accessor: 'stats.neutralRatio',
        valueTransformer: (value) => formatValue(value, 1, true, 1),
        sortable: true,
      },
      {
        Header: intl.formatMessage({ id: 'courseStatsTable.noAnswer' }),
        value: intl.formatMessage({ id: 'courseStatsTable.noAnswer' }),
        accessor: 'stats.noAnswerRatio',
        valueTransformer: (value) => formatValue(value, 1, true, 1),
        sortable: true,
      },
    ];

    return [
      {
        Header: intl.formatMessage({ id: 'subject' }),
        value: intl.formatMessage({ id: 'subject' }),
        accessor: 'name',
        sortable: true,
      },
      {
        Header: (
          <Box display="flex" flexDirection="row" alignItems="center">
            <Box mr={1}>
              <FormattedMessage id="students" />
            </Box>
            <Tooltip title={<FormattedMessage id="courseStats.studentsColumnTooltip" />}>
              <InfoIcon />
            </Tooltip>
          </Box>
        ),
        value: intl.formatMessage({ id: 'students' }),
        accessor: 'stats.answersRatio',
        valueTransformer: (value) => formatValue(value, 1),
        sortable: true,
      },
      {
        Header: intl.formatMessage({
          id: 'courseStatsTable.numberOfQuestions',
        }),
        value: intl.formatMessage({ id: 'courseStatsTable.numberOfQuestions' }),
        accessor: 'stats.totalQuestionsCount',
        sortable: true,
      },
      {
        Header: intl.formatMessage({
          id: 'courseStatsTable.numberOfDoneQuestions',
        }),
        value: intl.formatMessage({
          id: 'courseStatsTable.numberOfDoneQuestions',
        }),
        accessor: 'stats.doneQuestionsCount',
        sortable: true,
      },
      {
        Header: intl.formatMessage({ id: 'courseStatsTable.answersTypes' }),
        columns: answerTypesColumns,
      },
    ];
  }, [intl]);

  const filterKeys = ['name'];
  const filteredSubjects = filterData(subjects, query, filterKeys);

  return (
    <TSTable
      columns={columns}
      data={filteredSubjects}
      loading={loading}
      errorMessage={error}
      onSearch={setQuery}
      allowDownloadAsXLSX
      xlsxFileName={xlsxFileName}
      onSelectRow={onSelectSubject}
      onRefreshData={onRefetchStats}
    />
  );
};

export default CourseStatsTable;
